import { useAppDispatch, useAppSelector, useAppStore } from "@/app/appStore";
import {
  selectIsPlaybackLoaded,
  selectSelectedOrder,
  setHighlightedTimelineEventId,
  updateZoomLevel,
} from "@/entities/annotation";
import {
  PlaybackState,
  useMediaState,
  usePlayer,
  usePlayerStore,
} from "@tezzro/player";
import { useTimelineOptions } from "../hooks/useTimelineOptions";
import { ExtendedVisTimeline } from "@/shared/types";
import dayjs from "dayjs";
import moment from "moment";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useTrackedHotkeys } from "@/shared/hooks/useTrackedHotkeys";
import { useUpdateEffect } from "react-use";
import {
  DataGroupCollectionType,
  DataItemCollectionType,
  Timeline as VisTimeline,
} from "vis-timeline/esnext";
import { createPortal } from "react-dom";
import { CustomTimelineGroup, useTimelineData } from "../hooks/useTimelineData";
import BasicFilterSection from "./basic-filter-section";
import { TimelineGroupType } from "../lib/types";
import { calcZoomLevel } from "@/shared/helpers/timeline";
import { useDebouncedPostHogCapture } from "@/shared/hooks/useDebouncedPostHogCapture";

export type TimelineRef = {
  adjustZoomLevel(value: number): void;
  setTimelineRange(
    start: Date,
    end: Date,
    options?: {
      clearDataset?: boolean;
      forceZoom?: boolean;
    }
  ): void;
};

const TIME_MARKER_ID = "time-cursor";

const hasClass = (element: any, classname: string) => {
  while (element) {
    if (element.classList && element.classList.contains(classname)) {
      return true;
    }
    element = element.parentElement;
  }
  return false;
};

interface VisTimelineClickEventProps {
  event: MouseEvent;
  item?: number | string;
  group?: number | string;
  what?: string;
  pageX: number;
  pageY: number;
}

interface Group {
  index: number;
  id: string;
}

export const Timeline = forwardRef<TimelineRef>((_, ref) => {
  const {
    dataset,
    groups,
    showSavedOnly,
    setShowSavedOnly,
    setTimelineBackgroundRanges,
    updateTimelineBackgroundRanges,
    // toggleTimelineGroup,
    toggleSavedItemsVisibility,
    highlightTimelineItem,
  } = useTimelineData();

  const { getState } = usePlayerStore();
  const player = usePlayer();
  const container = useRef<HTMLDivElement | null>(null);
  const outerContainerRef = useRef<HTMLDivElement | null>(null);
  const [timeline, setTimeline] = useState<VisTimeline | null>(null);
  const isZooming = useRef(false);
  const isFocusAnimating = useRef(false);
  const isDragging = useRef(false);
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const selectedOrder = useAppSelector(selectSelectedOrder);
  const isPlaybackLoaded = useAppSelector(selectIsPlaybackLoaded);
  const selectedGroupsRef = useRef<Group[] | null>(null);
  const captureDebounced = useDebouncedPostHogCapture();

  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  );
  const showSavedOnlyRef = useRef(showSavedOnly);

  const handleHide = (item: TimelineGroupType) => {
    const isSelected = selectedGroupsRef.current?.find(
      (group) => group.id === item.id
    );
    const newHiddenState = !item.isHidden;

    if (isSelected && selectedGroupsRef.current) {
      const updatedGroups = selectedGroupsRef.current
        .map((selectedGroup) => {
          const group = groups.get(selectedGroup.id);

          if (group) {
            return {
              ...group,
              isHidden: newHiddenState,
            };
          }
          return null;
        })
        .filter((group) => group !== null);

      groups.update(updatedGroups);
    } else {
      // If the toggled item is not selected, only update itself
      groups.update({
        ...item,
        isHidden: newHiddenState,
      });
    }
  };

  const handleSave = (item: CustomTimelineGroup) => {
    const isSelected = selectedGroupsRef.current?.find(
      (group) => group.id === item.id
    );
    const newSavedState = !item.isSaved;

    if (isSelected && selectedGroupsRef.current) {
      const updatedGroups = selectedGroupsRef.current
        .map((selectedGroup) => {
          const group = groups.get(selectedGroup.id);

          if (group) {
            return {
              ...group,
              isSaved: newSavedState,
              visible: showSavedOnlyRef.current ? newSavedState : true,
            };
          }
          return null;
        })
        .filter((group) => group !== null);

      groups.update(updatedGroups);
    } else {
      // Update the base item
      groups.update({
        ...item,
        isSaved: newSavedState,
        visible: showSavedOnlyRef.current ? newSavedState : true,
      });

      // If item has nested groups, update them all
      if (item.nestedGroups && item.nestedGroups.length > 0) {
        item.nestedGroups.forEach((nestedId) => {
          const nestedGroup = groups.get(nestedId);
          if (nestedGroup) {
            groups.update({
              ...nestedGroup,
              isSaved: newSavedState,
              visible: showSavedOnlyRef.current ? newSavedState : true,
            });
          }
        });
      }

      if (item.parentGroup) {
        const parentGroup = groups.get(item.parentGroup);
        if (parentGroup && newSavedState) {
          // If saving, check all nested groups of the parent
          const allNestedGroupsSaved =
            parentGroup.nestedGroups?.every((nestedId) => {
              const nestedGroup = groups.get(nestedId);
              return nestedGroup?.isSaved === true;
            }) ?? false;

          if (allNestedGroupsSaved) {
            groups.update({
              ...parentGroup,
              isSaved: true,
              visible: showSavedOnlyRef.current ? true : true,
            });
          }
        }
      }
    }
  };

  useEffect(() => {
    showSavedOnlyRef.current = showSavedOnly;
  }, [showSavedOnly]);

  const resetHighlightedEvent = () => {
    dispatch(setHighlightedTimelineEventId(null));
    highlightTimelineItem(selectedOrder?.id || null);
  };

  const options = useTimelineOptions({ handleHide, handleSave });

  const onClick = async (props: VisTimelineClickEventProps) => {
    const { event, group: clickedGroupId } = props;
    const target = event.target as HTMLElement;
    const labelsetElement = document.querySelector(".vis-labelset");
    const foregroundElement = document.querySelector(".vis-foreground");
    const selectedClassName = "selected";

    if (typeof clickedGroupId !== "string") {
      return;
    }

    if (!labelsetElement || !foregroundElement) {
      console.log("Required elements not found");
      return;
    }

    let clickedElement: HTMLElement | null = null;
    let clickedGroupIndex = -1;

    // Determine if click was in labelset or foreground (or hide and save buttons)
    if (target.closest(".vis-labelset")) {
      if (!target.closest(".vis-inner")) {
        // toggleTimelineGroup(clickedGroupId);
        return;
      }

      clickedElement = target.closest(".vis-labelset > *") as HTMLElement;
      if (clickedElement) {
        clickedGroupIndex = Array.from(labelsetElement.children).indexOf(
          clickedElement
        );
      }
    } else if (target.closest(".vis-foreground")) {
      clickedElement = target.closest(".vis-foreground > *") as HTMLElement;
      if (clickedElement) {
        clickedGroupIndex = Array.from(foregroundElement.children).indexOf(
          clickedElement
        );
      }
    }

    if (clickedGroupIndex === -1) {
      return;
    }

    const isMultiSelectKey = event.metaKey || event.ctrlKey;

    if (!isMultiSelectKey) {
      if (
        selectedGroupsRef.current?.find(
          ({ index }) => index === clickedGroupIndex
        )
      ) {
        // Reset by removing 'selected' class from all elements
        const labelElements = labelsetElement.children;
        const foregroundElements = foregroundElement.children;

        for (let i = 0; i < labelElements.length; i++) {
          (labelElements[i] as HTMLElement).classList.remove(selectedClassName);
          (foregroundElements[i] as HTMLElement).classList.remove(
            selectedClassName
          );
        }

        selectedGroupsRef.current = [];
      } else {
        // Reset first
        const labelElements = labelsetElement.children;
        const foregroundElements = foregroundElement.children;

        for (let i = 0; i < labelElements.length; i++) {
          (labelElements[i] as HTMLElement).classList.remove(selectedClassName);
          (foregroundElements[i] as HTMLElement).classList.remove(
            selectedClassName
          );
        }

        selectedGroupsRef.current = [
          { index: clickedGroupIndex, id: clickedGroupId },
        ];
        (
          labelsetElement.children[clickedGroupIndex] as HTMLElement
        ).classList.add(selectedClassName);
        (
          foregroundElement.children[clickedGroupIndex] as HTMLElement
        ).classList.add(selectedClassName);
      }
    } else {
      if (!selectedGroupsRef.current) {
        selectedGroupsRef.current = [];
      }

      if (
        selectedGroupsRef.current.find(
          ({ index }) => index === clickedGroupIndex
        )
      ) {
        selectedGroupsRef.current = selectedGroupsRef.current.filter(
          ({ index }) => index !== clickedGroupIndex
        );
        (
          labelsetElement.children[clickedGroupIndex] as HTMLElement
        ).classList.remove(selectedClassName);
        (
          foregroundElement.children[clickedGroupIndex] as HTMLElement
        ).classList.remove(selectedClassName);
      } else {
        selectedGroupsRef.current.push({
          index: clickedGroupIndex,
          id: clickedGroupId,
        });
        (
          labelsetElement.children[clickedGroupIndex] as HTMLElement
        ).classList.add(selectedClassName);
        (
          foregroundElement.children[clickedGroupIndex] as HTMLElement
        ).classList.add(selectedClassName);
      }
    }
  };

  const onTimeChange = async (event: any) => {
    player.seek(event.time);
  };

  const updateZoomFromTimeline = useCallback(() => {
    if (!timeline || isZooming.current) return;

    const window = timeline.getWindow();
    if (!window) return;

    captureDebounced("timeline_zoom");

    const newZoom = calcZoomLevel(
      window.start,
      window.end,
      options.zoomMin as number,
      options.zoomMax as number,
      false
    );

    dispatch(updateZoomLevel(newZoom));
  }, [timeline, options.zoomMin, options.zoomMax, dispatch, captureDebounced]);

  const onRangeChange = (event: { byUser: boolean }) => {
    if (event.byUser) {
      isDragging.current = true;
    }
  };

  const onRangeChanged = (event: { byUser: boolean }) => {
    if (event.byUser) {
      isDragging.current = false;
    }
  };

  const onMouseDown = (event: { customTime: string }) => {
    if (event.customTime === "time-cursor") {
      resetHighlightedEvent();
    }
  };

  const onMouseUp = (event: { customTime: string }) => {
    if (event.customTime === "time-cursor") {
      resetHighlightedEvent();
    }
  };

  const onDoubleClick = (event: {
    time: Date;
    event: { target: HTMLElement };
  }) => {
    const { isPlaybackLoaded } = store.getState().annotation;

    if (!isPlaybackLoaded) return;

    resetHighlightedEvent();

    if (hasClass(event.event.target, "vis-foreground")) {
      const VIS_ITEM_CLASS_NAME = "vis-item-overflow";

      if (
        (event.event.target as HTMLElement).classList.contains(
          VIS_ITEM_CLASS_NAME
        )
      ) {
        return;
      }

      player.seek(event.time);

      return;
    }
  };

  const onFilterSaved = () => {
    toggleSavedItemsVisibility(!showSavedOnly);
    setShowSavedOnly(!showSavedOnly);
  };

  // Zoom in/out visible window relative to cursor/center
  const adjustZoomLevel = (zoomLevel: number) => {
    const currentTime = getState().currentTime;

    if (!timeline) return;

    isZooming.current = true;
    const window = timeline.getWindow();

    const currentWindowCenter = currentTime
      ? currentTime
      : (window.start.getTime() + window.end.getTime()) / 2;

    const zoomMin = options.zoomMin as number;
    const zoomMax = options.zoomMax as number;
    const zoomFactor = Math.exp(Math.log(zoomMax / zoomMin) * (1 - zoomLevel));
    const windowDuration = zoomMin * zoomFactor;

    const halfDuration = windowDuration / 2;
    const newStartDate = currentWindowCenter - halfDuration;
    const newEndDate = currentWindowCenter + halfDuration;

    dispatch(updateZoomLevel(zoomLevel));

    timeline.setWindow(
      new Date(newStartDate),
      new Date(newEndDate),
      {
        animation: !isFocusAnimating.current,
      },
      () => {
        isZooming.current = false;
      }
    );
  };

  // Set the timeline range and clear the dataset if needed
  const setTimelineRange: TimelineRef["setTimelineRange"] = (
    start,
    end,
    config
  ) => {
    if (timeline) {
      if (config?.clearDataset) {
        setTimelineBackgroundRanges(start, end);
      } else {
        updateTimelineBackgroundRanges(start, end);
      }

      const currentZoom = store.getState().annotation.zoomLevel;

      if (config?.forceZoom || currentZoom === null) {
        const GAP_IN_MINUTES = 1;
        const startWithGap = dayjs(start)
          .subtract(GAP_IN_MINUTES, "minute")
          .toDate();
        const endWithGap = dayjs(end).add(GAP_IN_MINUTES, "minute").toDate();

        setTimeout(() => {
          const newZoomLevel = calcZoomLevel(
            startWithGap,
            endWithGap,
            options.zoomMin as number,
            options.zoomMax as number
          );
          dispatch(updateZoomLevel(newZoomLevel));
          timeline.setWindow(startWithGap, endWithGap, {
            animation: true,
          });
        });
      } else {
        const center = (start.getTime() + end.getTime()) / 2;
        const maxRange = options.zoomMax as number;
        const flexibleStart = center - maxRange / 2;
        const flexibleEnd = center + maxRange / 2;

        timeline.setOptions({
          start: flexibleStart,
          end: flexibleEnd,
        });
        adjustZoomLevel(currentZoom);
      }
    }
  };

  const seekVideo = (seekDirection: -1 | 1) => {
    resetHighlightedEvent();

    const seekAmountStr = store.getState().annotation.seekAmount;
    const seekAmountValue = parseFloat(seekAmountStr) * 1000;
    const currentTime = getState().currentTime;

    const newTime = currentTime + seekDirection * seekAmountValue;
    player.seek(newTime);
  };

  useTrackedHotkeys("left", () => seekVideo(-1), {
    enabled: isPlaybackLoaded,
  });
  useTrackedHotkeys("right", () => seekVideo(1), {
    enabled: isPlaybackLoaded,
  });

  useImperativeHandle(ref, () => ({
    adjustZoomLevel,
    setTimelineRange,
  }));

  useEffect(() => {
    const currentTime = new Date();

    try {
      timeline?.setCustomTime(currentTime, TIME_MARKER_ID);
    } catch {
      timeline?.addCustomTime(currentTime, TIME_MARKER_ID);
    }

    timeline?.focus(TIME_MARKER_ID);
  }, []);

  useEffect(() => {
    if (!container.current) return;

    const defaultCurrentTime = dayjs().startOf("day").toDate();
    const timelineInstance = new VisTimeline(
      container.current,
      dataset as DataItemCollectionType,
      groups as DataGroupCollectionType,
      options
    );

    timelineInstance.setOptions({
      moment: (date: moment.MomentInput) => moment(date).utc(),
    });
    setTimeline(timelineInstance as ExtendedVisTimeline);

    try {
      timelineInstance.setCustomTime(defaultCurrentTime, TIME_MARKER_ID);
    } catch {
      timelineInstance.addCustomTime(defaultCurrentTime, TIME_MARKER_ID);
    }

    timelineInstance.focus(TIME_MARKER_ID);

    timelineInstance.on("click", onClick);
    timelineInstance.on("timechange", onTimeChange);
    timelineInstance.on("rangechange", onRangeChange);
    timelineInstance.on("rangechanged", onRangeChanged);
    timelineInstance.on("doubleClick", onDoubleClick);
    timelineInstance.on("mouseDown", onMouseDown);
    timelineInstance.on("mouseUp", onMouseUp);

    return () => {
      timelineInstance.off("click", onClick);
      timelineInstance.off("timechange", onTimeChange);
      timelineInstance.off("rangechange", onRangeChange);
      timelineInstance.off("rangechanged", onRangeChanged);
      timelineInstance.off("doubleClick", onDoubleClick);
      timelineInstance.off("mouseDown", onMouseDown);
      timelineInstance.off("mouseUp", onMouseUp);
    };
  }, []);

  useEffect(() => {
    if (!outerContainerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (timeline) {
          timeline.setOptions({
            height: `${entry.contentRect.height - 64}px`,
          });
        }
      }
    });

    resizeObserver.observe(outerContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [timeline]);

  useEffect(() => {
    const targetSection = document.querySelector(
      ".vis-timeline.vis-bottom.vis-ltr"
    ) as HTMLElement | null;

    if (targetSection) {
      targetSection.style.position = "relative";
      setPortalContainer(targetSection);
    } else {
      console.warn("Target section not found");
    }
  }, []);

  return (
    <div
      ref={outerContainerRef}
      className={`relative timeline-outer-container h-full ${
        !isPlaybackLoaded ? "timeline-cursor-disabled" : ""
      }`}
    >
      <TimelineBar
        timeline={timeline}
        updateZoomFromTimeline={updateZoomFromTimeline}
      />
      {portalContainer &&
        createPortal(
          <BasicFilterSection
            showSavedOnly={showSavedOnly}
            hasShowSavedButton
            onFilterToggle={() => onFilterSaved()}
          />,
          portalContainer
        )}

      <div ref={container} className="timeline-container h-full" />
    </div>
  );
});

function TimelineBar({
  timeline,
  updateZoomFromTimeline,
}: {
  timeline: VisTimeline | null;
  updateZoomFromTimeline: () => void;
}) {
  const currentTime = useMediaState("currentTime");
  const playbackState = useMediaState("playbackState");
  const timebarRef = useRef<HTMLDivElement | null>(null);
  const isFocusAnimating = useRef(false);
  const isDragging = useRef(false);

  useEffect(() => {
    const onRangeChange = (event: any) => {
      if (event.byUser) {
        updateZoomFromTimeline();
        isDragging.current = true;
      }
    };

    const onRangeChanged = (event: any) => {
      if (event.byUser) {
        isDragging.current = false;
      }
    };

    timeline?.on("rangechange", onRangeChange);
    timeline?.on("rangechanged", onRangeChanged);

    return () => {
      timeline?.off("rangechange", onRangeChange);
      timeline?.off("rangechanged", onRangeChanged);
    };
  }, [timeline, updateZoomFromTimeline]);

  useUpdateEffect(() => {
    if (!timeline) return;

    try {
      timeline.setCustomTime(currentTime, TIME_MARKER_ID);
    } catch {
      timeline.addCustomTime(currentTime, TIME_MARKER_ID);
    }

    adjustWindowAndfocusCursor(currentTime);
  }, [currentTime]);

  const isPlaying = () => {
    return playbackState === PlaybackState.Playing;
  };

  const adjustWindowAndfocusCursor = (currentTime: number) => {
    if (!timeline || isFocusAnimating.current) return;

    const { start, end } = timeline.getWindow();
    const windowDuration = end.getTime() - start.getTime();
    const shift = isPlaying() ? windowDuration * 0.05 : windowDuration * 0.5; // currentTime will be in the center of the window

    if (
      (currentTime >= end.getTime() || currentTime < start.getTime()) &&
      !isDragging.current
    ) {
      isFocusAnimating.current = true;
      timeline?.setWindow(
        new Date(currentTime - shift),
        new Date(currentTime + windowDuration - shift),
        {
          animation: false,
        },
        function onAnimationEnd() {
          isFocusAnimating.current = false;
        }
      );
    }
  };

  return <div id="timebar" ref={timebarRef} />;
}
